import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { COLORS, CommonLinkProps } from '../Link/common.style';
import RouterNavLink from '../Link/RouterNavLink';

const Wrap = styled.div<{ $padding?: string }>`
  display: flex;
  ${({ $padding }) =>
    $padding ? `padding: ${$padding};` : 'padding: 0 8px 0 8px;'}

  .isMobileStyle & {
    ${({ $padding }) =>
      $padding ? `padding: ${$padding};` : 'padding: 0 8px 0 8px;'} /*  */
        
    /* Add scrollbar as last resort
       CommentedOut cause it's Causing problems with underline */
    overflow-x: auto;
    /* overflow-y: hidden; */
    scrollbar-width: none;
  }
`;

// TODO: no longer needed?
const takeColor =
  (fallbackColor: string) =>
  ({ $color }: { $color: CommonLinkProps['color'] }) =>
    !$color || $color === 'inherit'
      ? fallbackColor
      : COLORS[$color] || fallbackColor;

const buttonStyle = css<{ $color: CommonLinkProps['color'] }>`
  position: relative;
  top: 1px;
  cursor: pointer;
  padding: 12px 16px;
  font-size: 12px;
  color: ${takeColor('var(--gray4c)')};
  white-space: nowrap;
  &:hover {
    color: var(--gray4);
  }
  .isMobileStyle & {
    padding: 12px 4px;
  }
`;

const Tab = styled.div<{
  $isSelected: boolean;
  $stretch?: boolean;
  $underline: boolean;
  $color?: CommonLinkProps['color'];
}>`
  ${({ $stretch }) => ($stretch ? 'flex: 1;' : '')}
  display: flex;
  align-items: center;
  justify-content: center;
  &&:hover {
    color: var(--secondaryHover);
  }
  ${({ $underline }) =>
    $underline ? 'border-bottom: 1px solid var(--separator);' : null}

  ${({ $isSelected, $color }) =>
    $isSelected
      ? `
      && {
        color: ${takeColor('var(--secondary)')({ $color })};
        border-color: ${takeColor('var(--secondary)')({ $color })};
      }
      &&:hover {
        color: ${takeColor('var(--secondary)')({ $color })};
      }
      `
      : ''}
`;

const LastTab = styled(Tab)`
  position: relative;
  top: 1px;
  padding: 0;
`;

const RightContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
`;

const TabButton = styled(Tab)<{ $color: CommonLinkProps['color'] }>`
  ${buttonStyle}
`;

const StyledNavLink = styled(RouterNavLink)<{
  $color: CommonLinkProps['color'];
  $underline: boolean;
  $stretch: boolean;
}>`
  ${({ $stretch }) => ($stretch ? 'flex: 1;' : '')}

  display: flex;
  align-items: center;
  justify-content: center;

  ${buttonStyle}
  color: ${takeColor('var(--gray4c)')};

  &.active {
    /* color: var(--mainLight); */
    color: ${({ $color }) => takeColor('white')({ $color })};

    ${({ $underline }) =>
      $underline ? 'border-bottom: 1px solid var(--separator);' : null}
  }
`;

// TODO: bubble component
const Bubble = styled.div<{ $active: boolean }>`
  color: #fff;
  text-align: center;
  font-family: var(--standardFontFamily);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  border-radius: 2px;
  background: #3c414a;
  padding: 1px 4px;
  margin-left: 4px;
  ${({ $active }) => ($active ? '' : 'opacity: 0.4;')}
`;

type Props = {
  buttons: (
    | {
        title: string;
        titleBubble?: string;
        onClick: () => void;
        navigateTo?: undefined;
        navigateIsEnd?: undefined;
        active: boolean;
        stretch?: boolean;
        underline?: boolean;
        color?: CommonLinkProps['color'];
      }
    | {
        title: string;
        titleBubble?: string;
        onClick?: undefined;
        navigateTo: string;
        navigateIsEnd?: boolean;
        active?: undefined;
        stretch?: boolean;
        underline?: boolean;
        color?: CommonLinkProps['color'];
      }
  )[];
  lastButton?: {
    element: ReactNode;
    active: boolean;
  };
  rightContent?: ReactNode;
  padding?: string;
};

export default function TabsNavigation({
  buttons,
  padding,
  lastButton,
  rightContent,
}: Props) {
  return (
    <Wrap $padding={padding}>
      {buttons.map(
        ({
          title,
          titleBubble,
          onClick,
          navigateTo,
          navigateIsEnd,
          active,
          stretch,
          underline,
          color,
        }) =>
          navigateTo ? (
            <StyledNavLink
              key={title}
              to={navigateTo}
              end={navigateIsEnd}
              $color={color}
              $stretch={!!stretch}
              $underline={!!underline}
            >
              {title}
              {titleBubble && <Bubble $active={!!active}>{titleBubble}</Bubble>}
            </StyledNavLink>
          ) : (
            <TabButton
              key={title}
              onClick={onClick}
              $isSelected={!!active}
              $stretch={stretch}
              $color={color}
              $underline={!!underline}
            >
              {title}
              {titleBubble && <Bubble $active={!!active}>{titleBubble}</Bubble>}
            </TabButton>
          )
      )}
      {lastButton && (
        <LastTab $isSelected={lastButton.active} $underline={false}>
          {lastButton.element}
        </LastTab>
      )}
      {rightContent && <RightContent>{rightContent}</RightContent>}
    </Wrap>
  );
}
